import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../context/AuthContext";
import { canVerTodasLojasUser } from "../../../lib/screenAccess";
import { useUsuarios } from "../../../hooks/useUsuarios";
import { useFilaEntregas } from "../../../hooks/useFilaEntrega";
import {
  useCreateFilaEntrega,
} from "../../../hooks/useFilaEntrega";
import { useEntregasAtivasOperacao } from "../../../hooks/useEntrega";
import { useRankingEvents } from "../../../hooks/useRankingEvents";
import { RANKING_SSE_QUERY_KEYS } from "../../../constants/rankingEventQueryKeys";
import { entregaService, Entrega } from "../../../services/entregaService";
import {
  FilaEntrega,
  buildFilaEntregaDiaAtualFilters,
} from "../../../services/filaEntregaService";
import { Usuario } from "../../../types/usuario";
import { broadcastDashboardRefresh } from "../../../utils/dashboardRefreshBroadcast";
import { broadcastPainelRefresh } from "../../../utils/painelRefreshBroadcast";
import { lojaService } from "../../../services/lojaService";
import { BrazilDate } from "../../../utils/BrazilDate";
import { toast } from "react-toastify";
import { Loja } from "../../../types/loja";
import {
  getMsRestantesParaFinalizar,
  MIN_MINUTOS_FINALIZAR_ENTREGA,
} from "../entregaTempoMinimo";

interface EntregadorOperando {
  entregador: Usuario;
  filaEntrega: FilaEntrega;
  entregaAtiva?: Entrega;
}

interface EntregadorContextType {
  lojaId: number | undefined;
  lojas: Loja[];
  selectedLojaId: number | undefined;
  setSelectedLojaId: (id: number | undefined) => void;
  showLojaSelector: boolean;
  entregadores: Usuario[];
  filaEntregas: FilaEntrega[];
  entregasAtivas: Entrega[];
  entregadoresSemFila: Usuario[];
  entregadoresOperando: EntregadorOperando[];
  entregadorRoleId: number | null;
  isLoading: boolean;
  isLoadingOperando: boolean;
  isFinalizando: boolean;
  filaFinalizandoId: number | null;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  handleIniciarDia: (entregadorId: number) => Promise<void>;
  handleFinalizar: (
    filaEntregaId: number,
    entregaId?: number,
    statusFila?: string,
    temEntregaAtiva?: boolean
  ) => Promise<void>;
}

const EntregadorContext = createContext<EntregadorContextType | undefined>(
  undefined
);

export const EntregadorProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const podeVerTodasLojas = canVerTodasLojasUser(user);
  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    () => user?.lojaId
  );

  const effectiveLojaId = podeVerTodasLojas
    ? (selectedLojaId ?? user?.lojaId)
    : user?.lojaId;
  const lojaId = effectiveLojaId;

  const { data: lojas = [] } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [liveUpdatesEnabled, setLiveUpdatesEnabled] = useState(false);
  const [isFinalizando, setIsFinalizando] = useState(false);
  const [filaFinalizandoId, setFilaFinalizandoId] = useState<number | null>(
    null
  );
  const finalizandoRef = useRef(false);
  const [sseAvailable, setSseAvailable] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => setLiveUpdatesEnabled(true), 1500);
    return () => window.clearTimeout(timer);
  }, []);

  const usuarioFilters = lojaId
    ? {
        lojaId,
        ativo: true,
        limit: 200,
      }
    : undefined;

  const { data: entregadoresResponse, isLoading: isLoadingEntregadores } =
    useUsuarios(usuarioFilters, { enabled: !!lojaId });

  const entregadores = useMemo(
    () =>
      (entregadoresResponse?.data ?? [])
        .filter(
          (u) =>
            u.role?.nome === "Entregador" || u.role?.nome === "Entregadores"
        )
        .filter((u) => u.ativo),
    [entregadoresResponse?.data]
  );

  const operacaoRefetchOptions = {
    refetchInterval: sseAvailable ? (false as const) : 60_000,
    refetchOnWindowFocus: false,
  };

  const { data: filaEntregas = [], isLoading: isLoadingFila } = useFilaEntregas(
    lojaId
      ? buildFilaEntregaDiaAtualFilters(lojaId, {
          statusIn: ["aguardando", "em_andamento"],
        })
      : undefined,
    { ...operacaoRefetchOptions, fetchAllPages: false }
  );

  const { data: entregasAtivasOperacao = [], isLoading: isLoadingEntregas } =
    useEntregasAtivasOperacao(lojaId, operacaoRefetchOptions);

  const entregasAtivas = useMemo((): Entrega[] => {
    if (!lojaId) return [];
    return entregasAtivasOperacao.map((row) => ({
      id: row.id,
      dataInicio: row.dataInicio,
      dataFim: row.dataFim,
      tempoTotal: null,
      status: row.status,
      quantidadeEnderecos: row.quantidadeEnderecos,
      loja: { id: lojaId, nome: "" },
      entregador: row.entregador,
      createdAt: row.dataInicio,
      updatedAt: row.dataInicio,
    }));
  }, [entregasAtivasOperacao, lojaId]);

  const createFilaMutation = useCreateFilaEntrega();

  useRankingEvents("entrega", lojaId ?? null, {
    queryKeys: RANKING_SSE_QUERY_KEYS.entregador,
    enabled: liveUpdatesEnabled,
    onAvailabilityChange: setSseAvailable,
  });

  const entregadoresSemFila = useMemo(() => {
    if (!entregadores || !filaEntregas) return [];

    const entregadoresNaFila = new Set(
      filaEntregas
        .filter(
          (fila) =>
            fila.status === "aguardando" || fila.status === "em_andamento"
        )
        .map((fila) => fila.entregadorId)
    );

    const semFila = entregadores.filter(
      (entregador) => !entregadoresNaFila.has(entregador.id)
    );

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      return semFila.filter((entregador) =>
        entregador.nome.toLowerCase().includes(term)
      );
    }

    return semFila;
  }, [entregadores, filaEntregas, searchTerm]);

  const entregadoresOperando = useMemo(() => {
    if (!entregadores || !filaEntregas) return [];

    const entregadoresComFila: EntregadorOperando[] = [];
    const filaIdsProcessados = new Set<number>();

    filaEntregas
      .filter(
        (fila) => fila.status === "aguardando" || fila.status === "em_andamento"
      )
      .forEach((fila) => {
        if (filaIdsProcessados.has(fila.id!)) {
          return;
        }
        filaIdsProcessados.add(fila.id!);

        const entregador = entregadores.find((e) => e.id === fila.entregadorId);
        if (entregador) {
          entregadoresComFila.push({
            entregador,
            filaEntrega: fila,
            entregaAtiva: entregasAtivas.find(
              (e) => e.entregador.id === fila.entregadorId
            ),
          });
        }
      });

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      return entregadoresComFila.filter((item) =>
        item.entregador.nome.toLowerCase().includes(term)
      );
    }

    return entregadoresComFila;
  }, [entregadores, filaEntregas, entregasAtivas, searchTerm]);

  const handleIniciarDia = async (entregadorId: number) => {
    if (!lojaId) {
      toast.error("Erro: Loja não identificada");
      return;
    }

    try {
      await createFilaMutation.mutateAsync({
        dataFila: BrazilDate.now().toISOString(),
        lojaId,
        entregadorId,
        status: "aguardando",
      });
      toast.success("Dia iniciado com sucesso!");
    } catch (error: any) {
      toast.error(
        "Erro ao iniciar dia: " + (error?.message || "Erro desconhecido")
      );
    }
  };

  const refetchEntregadorOperacao = useCallback(async () => {
    await Promise.all([
      queryClient.refetchQueries({
        queryKey: ["entregas-ativas-operacao", lojaId],
      }),
      queryClient.refetchQueries({ queryKey: ["filaEntregas"] }),
      queryClient.refetchQueries({ queryKey: ["rankingDiaEntrega"] }),
      queryClient.refetchQueries({ queryKey: ["entregas"] }),
    ]);
    broadcastPainelRefresh();
    broadcastDashboardRefresh();
  }, [lojaId, queryClient]);

  const handleFinalizar = useCallback(
    async (
      filaEntregaId: number,
      entregaId?: number,
      statusFila?: string,
      _temEntregaAtiva?: boolean
    ) => {
      if (finalizandoRef.current || isFinalizando) {
        return;
      }

      const isFilaEmAndamento = statusFila === "em_andamento";
      const fila = filaEntregas.find((f) => f.id === filaEntregaId);

      let effectiveEntregaId = entregaId;
      let dataInicioEntrega = effectiveEntregaId
        ? entregasAtivas.find((e) => e.id === effectiveEntregaId)?.dataInicio
        : undefined;

      const restanteAntes = getMsRestantesParaFinalizar(dataInicioEntrega);
      if (restanteAntes > 0) {
        toast.error(
          `Aguarde ${Math.ceil(restanteAntes / 60_000)} min. Só é possível finalizar após ${MIN_MINUTOS_FINALIZAR_ENTREGA} minutos em rota.`
        );
        return;
      }

      finalizandoRef.current = true;
      setIsFinalizando(true);
      setFilaFinalizandoId(filaEntregaId);

      try {
        if (!effectiveEntregaId && fila?.entregadorId && lojaId) {
          const ativas = await entregaService.getAtivasOperacao(lojaId);
          const ativa = ativas.find(
            (e) => e.entregadorId === fila.entregadorId
          );
          effectiveEntregaId = ativa?.id;
          dataInicioEntrega = ativa?.dataInicio;
        }

        const restanteMs = getMsRestantesParaFinalizar(dataInicioEntrega);
        if (restanteMs > 0) {
          toast.error(
            `Aguarde ${Math.ceil(restanteMs / 60_000)} min. Só é possível finalizar após ${MIN_MINUTOS_FINALIZAR_ENTREGA} minutos em rota.`
          );
          return;
        }

        if (isFilaEmAndamento && !effectiveEntregaId) {
          toast.error(
            "Não foi possível encontrar a entrega ativa. Tente atualizar a página."
          );
        } else if (effectiveEntregaId) {
          try {
            await entregaService.finalizar(effectiveEntregaId);
          } catch (error: unknown) {
            const message =
              error instanceof Error ? error.message : String(error);
            if (!message.includes("já foi finalizada")) {
              throw error;
            }
          }
          await refetchEntregadorOperacao();
          toast.success(
            "Entrega finalizada! Entregador continua na fila aguardando."
          );
        }
      } catch (error: unknown) {
        toast.error(
          "Erro ao finalizar: " +
            (error instanceof Error ? error.message : "Erro desconhecido")
        );
      } finally {
        finalizandoRef.current = false;
        setIsFinalizando(false);
        setFilaFinalizandoId(null);
      }
    },
    [
      entregasAtivas,
      filaEntregas,
      isFinalizando,
      lojaId,
      refetchEntregadorOperacao,
    ]
  );

  const entregadorRoleId =
    entregadores.find((u) => u.role?.id != null)?.role?.id ?? null;

  const isLoading = isLoadingEntregadores || isLoadingFila;
  const isLoadingOperando = isLoadingEntregas;

  return (
    <EntregadorContext.Provider
      value={{
        lojaId,
        lojas,
        selectedLojaId,
        setSelectedLojaId,
        showLojaSelector: podeVerTodasLojas,
        entregadores,
        filaEntregas,
        entregasAtivas,
        entregadoresSemFila,
        entregadoresOperando,
        entregadorRoleId,
        isLoading,
        isLoadingOperando,
        isFinalizando,
        filaFinalizandoId,
        searchTerm,
        setSearchTerm,
        handleIniciarDia,
        handleFinalizar,
      }}
    >
      {children}
    </EntregadorContext.Provider>
  );
};

export const useEntregador = () => {
  const context = useContext(EntregadorContext);
  if (!context) {
    throw new Error("useEntregador must be used within EntregadorProvider");
  }
  return context;
};
